import { useQuery } from '@tanstack/react-query';
import api from '../services/authService';

/**
 * Custom hook for managing admin dashboard data
 * Handles fetching recent bookings, vendors, payments, and performance data
 */
export const useAdminDashboard = (period = 'month') => {
  // Fetch recent bookings
  const { 
    data: recentBookingsData, 
    isLoading: isLoadingBookings,
    error: bookingsError,
    refetch: refetchBookings
  } = useQuery({
    queryKey: ['adminRecentBookings'],
    queryFn: async () => {
      const response = await api.get('/admin/bookings?per_page=5&sort_by=created_at&sort_order=desc');
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // Data considered fresh for 2 minutes
  });

  // Fetch recent vendors
  const { 
    data: recentVendorsData, 
    isLoading: isLoadingVendors,
    error: vendorsError,
    refetch: refetchVendors
  } = useQuery({
    queryKey: ['adminRecentVendors'],
    queryFn: async () => {
      const response = await api.get('/admin/vendors?per_page=5&sort_by=created_at&sort_order=desc');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  // Fetch recent payments
  const { 
    data: recentPaymentsData, 
    isLoading: isLoadingPayments,
    error: paymentsError,
    refetch: refetchPayments
  } = useQuery({
    queryKey: ['adminRecentPayments'],
    queryFn: async () => {
      const response = await api.get('/admin/payments?per_page=5&sort_by=created_at&sort_order=desc');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  // Fetch performance data
  const { 
    data: performanceData, 
    isLoading: isLoadingPerformance,
    error: performanceError,
    refetch: refetchPerformance
  } = useQuery({
    queryKey: ['adminPerformance', period],
    queryFn: async () => {
      const response = await api.get(`/admin/performance?period=${period}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
  });

  // Extract data from responses
  const recentBookings = recentBookingsData?.data?.data || [];
  const recentVendors = recentVendorsData?.data?.data || [];
  const recentPayments = recentPaymentsData?.data?.data || [];
  const performance = performanceData?.data || {};

  // Combined loading state
  const isLoading = isLoadingBookings || isLoadingVendors || isLoadingPayments || isLoadingPerformance;

  // Combined error state
  const error = bookingsError || vendorsError || paymentsError || performanceError;

  // Refetch all data
  const refetchAll = () => {
    refetchBookings();
    refetchVendors();
    refetchPayments();
    refetchPerformance();
  };

  return {
    // Data
    recentBookings,
    recentVendors,
    recentPayments,
    performance,
    
    // Loading states
    isLoading,
    isLoadingBookings,
    isLoadingVendors,
    isLoadingPayments,
    isLoadingPerformance,
    
    // Error states
    error,
    bookingsError,
    vendorsError,
    paymentsError,
    performanceError,
    
    // Actions
    refetchAll,
    refetchBookings,
    refetchVendors,
    refetchPayments,
    refetchPerformance,
  };
};

